/**
 * Account Model - Processes API responses for account data
 */

/**
 * Process account creation response
 * @param {Object} response - API response from account create endpoint
 * @returns {Object} Formatted creation response
 */
export const accountCreatePost = (response) => {
    // Based on Postman collection response format
    if (response && response.data) {
        return {
            success: response.data.success === true,
            message: response.data.message || 'Account created successfully',
            data: response.data.data || null
        };
    }
    return {
        success: false,
        message: 'Failed to create account',
        data: null
    };
};

/**
 * Account list response data
 * @param {Object} response - API response from account list endpoint
 * @returns {Object} Account list data
 */
export const accountListGet = (response) => {
    if (response && response.data && response.data.success === true) {
        const accounts = Array.isArray(response.data.data) ? response.data.data.map(account => ({
            id: account?.id || null,
            slack: account?.slack || null,
            account_code: account?.account_code || '',
            label: account?.label || account?.account_name || account?.name || '',
            name: account?.name || account?.account_name || '',
            account_name: account?.account_name || account?.name || '',
            account_type: account?.account_type || '',
            parent_code: account?.parent_code || '',
            is_system: account?.is_system ?? 0,
            is_inherited: account?.is_inherited ?? 0,
            pos_default_account: account?.pos_default_account ?? 0,
            status: account?.status ? {
                label: account?.status?.label || '',
                color: account?.status?.color || ''
            } : null,
            store_name: account?.store_name || '',
            created_by: account?.created_by || '',
            createdAt: account?.createdAt || null,
            updatedAt: account?.updatedAt || null
        })) : [];
        return {
            success: true,
            total: response.data.total || accounts.length,
            data: accounts
        };
    }
    return {
        success: false,
        message: response?.data?.message || 'Failed to fetch account list',
        data: []
    };
};

/**
 * Account type list response data
 * @param {Object} response - API response from account type list endpoint
 * @returns {Object} Account type list data
 */
export const accountTypeListGet = (response) => {
    // Based on Postman collection response format
    if (response && response.data && response.data.success === true) {
        return {
            success: true,
            total: response.data.total || response.data.data.length,
            data: response.data.data
        };
    }
    return {
        success: false,
        message: response?.data?.message || 'Failed to fetch account type list',
        data: []
    };
};

/**
 * Process single account details response
 * @param {Object} response - API response from account details endpoint
 * @returns {Object} Formatted account details
 */
export const accountDetailsGet = (response) => {
    if (response && response.data && response.data.success === true) {
        const item = response.data.data || {};
        return {
            success: true,
            data: {
                id: item?.id || null,
                slack: item?.slack || null,
                store_id: item?.store_id || null,
                account_code: item?.account_code || '',
                account_type: item?.account_type || null,
                label: item?.label || '',
                initial_balance: item?.initial_balance || '0',
                description: item?.description || '',
                pos_default: item?.pos_default ?? 0,
                status: item?.status ?? 0,
                created_by: item?.created_by || null,
                updated_by: item?.updated_by || null,
                createdAt: item?.createdAt || null,
                updatedAt: item?.updatedAt || null,
                status_data: item?.status_data ? {
                    id: item?.status_data?.id || null,
                    key: item?.status_data?.key || '',
                    value: item?.status_data?.value ?? null,
                    value_constant: item?.status_data?.value_constant || '',
                    label: item?.status_data?.label || '',
                    color: item?.status_data?.color || '',
                    status: item?.status_data?.status ?? 0,
                    createdAt: item?.status_data?.createdAt || null,
                    updatedAt: item?.status_data?.updatedAt || null
                } : null,
                CreatedUser: item?.CreatedUser ? {
                    id: item?.CreatedUser?.id || null,
                    profile_img_url: item?.CreatedUser?.profile_img_url || null,
                    first_name: item?.CreatedUser?.first_name || '',
                    last_name: item?.CreatedUser?.last_name || '',
                    contact_number: item?.CreatedUser?.contact_number || '',
                    email: item?.CreatedUser?.email || '',
                    language_id: item?.CreatedUser?.language_id || null,
                    is_active: item?.CreatedUser?.is_active ?? false,
                    created_by: item?.CreatedUser?.created_by || null,
                    createdAt: item?.CreatedUser?.createdAt || null,
                    updatedAt: item?.CreatedUser?.updatedAt || null
                } : null,
                UpdatedUser: item?.UpdatedUser ? {
                    id: item?.UpdatedUser?.id || null,
                    profile_img_url: item?.UpdatedUser?.profile_img_url || null,
                    first_name: item?.UpdatedUser?.first_name || '',
                    last_name: item?.UpdatedUser?.last_name || '',
                    contact_number: item?.UpdatedUser?.contact_number || '',
                    email: item?.UpdatedUser?.email || '',
                    language_id: item?.UpdatedUser?.language_id || null,
                    is_active: item?.UpdatedUser?.is_active ?? false,
                    created_by: item?.UpdatedUser?.created_by || null,
                    createdAt: item?.UpdatedUser?.createdAt || null,
                    updatedAt: item?.UpdatedUser?.updatedAt || null
                } : null,
                account_type_data: item?.account_type_data ? {
                    id: item?.account_type_data?.id || null,
                    account_type_constant: item?.account_type_data?.account_type_constant || '',
                    label: item?.account_type_data?.label || '',
                    description: item?.account_type_data?.description || '',
                    status: item?.account_type_data?.status ?? 0,
                    created_by: item?.account_type_data?.created_by || null,
                    updated_by: item?.account_type_data?.updated_by || null,
                    createdAt: item?.account_type_data?.createdAt || null,
                    updatedAt: item?.account_type_data?.updatedAt || null
                } : null
            }
        };
    }
    return {
        success: false,
        message: response?.data?.message || 'Failed to fetch account details',
        data: {}
    };
};

/**
 * Process account update response
 * @param {Object} response - API response from account update endpoint
 * @returns {Object} Formatted update response
 */
export const accountUpdatePut = (response) => {
    // Based on Postman collection response format
    if (response && response.data) {
        return {
            success: response.data.success === true,
            message: response.data.message || 'Account updated successfully',
            data: response.data.data || null
        };
    }
    return {
        success: false,
        message: 'Failed to update account',
        data: null
    };
};

/**
 * Process account delete response
 * @param {Object} response - API response from account delete endpoint
 * @returns {Object} Formatted delete response
 */
export const accountDeleteDel = (response) => {
    // Based on Postman collection response format
    if (response && response.data) {
        return {
            success: response.data.success === true,
            message: response.data.message || 'Account deleted successfully'
        };
    }
    return {
        success: false,
        message: 'Failed to delete account'
    };
};